import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { SrIcon } from "@/components/ui/SrIcon";
import { cn } from "@/lib/utils";
import { REACTION_EMOJIS, REACTION_LABELS, type ReactionEmoji, type ReactionSummary } from "@/lib/chat-room/reactions";

interface ReactionBarProps {
  reactions: ReactionSummary[];
  onToggle: (emoji: ReactionEmoji, mine: boolean) => void;
  userName: (userId: string) => string | null;
  disabled?: boolean;
}

/** Reaksjonsbrikker under en melding, og knapp for å legge til en ny. */
export function ReactionBar({ reactions, onToggle, userName, disabled }: ReactionBarProps) {
  const [open, setOpen] = useState(false);
  const mineByEmoji = new Map(reactions.map((r) => [r.emoji, r.mine]));

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {reactions.map((r) => {
        const names = r.userIds.map((id) => userName(id) ?? "Ukjent").join(", ");
        return (
          <button
            key={r.emoji}
            type="button"
            aria-pressed={r.mine}
            aria-label={`${REACTION_LABELS[r.emoji]}: ${r.count}. ${names}`}
            title={names}
            disabled={disabled}
            onClick={() => onToggle(r.emoji, r.mine)}
            className={cn(
              "h-7 rounded-full border-[1.5px] px-2.5 text-[13px] transition-colors",
              r.mine ? "border-primary bg-primary/10 text-primary" : "border-border bg-card hover:bg-secondary",
            )}
          >
            {r.emoji} {r.count}
          </button>
        );
      })}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="Legg til reaksjon"
            disabled={disabled}
            className="flex h-7 w-7 items-center justify-center rounded-full border-[1.5px] border-border bg-card text-muted-foreground hover:bg-secondary"
          >
            <SrIcon name="smile-plus" className="h-3.5 w-3.5" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="flex w-auto gap-1 p-1.5">
          {REACTION_EMOJIS.map((emoji) => (
            <button
              key={emoji}
              type="button"
              aria-label={REACTION_LABELS[emoji]}
              onClick={() => {
                onToggle(emoji, mineByEmoji.get(emoji) ?? false);
                setOpen(false);
              }}
              className="flex h-9 w-9 items-center justify-center rounded-md text-lg hover:bg-secondary"
            >
              {emoji}
            </button>
          ))}
        </PopoverContent>
      </Popover>
    </div>
  );
}
